/**
 * ThemeV2PreviewCarousel - Screen preview grid for V2 theme editor
 *
 * Shows FlowDocument StepNode screens in a responsive grid using
 * V2ScreenView (native V2 renderer) inside DeviceMockup.
 *
 * Key difference from V1: uses V2ScreenView directly with StepNode,
 * no V1 adapter needed.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Button, Checkbox, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { V2ScreenView, AutoScaleScreenContainer } from '@modules/screen-view-v2';
// Deep import: the pure resolver only (the barrel pulls the whole runtime).
import { resolveScreenAnimationPlan } from '@modules/screen-view-v2/animations/resolveScreenAnimationPlan';
import { DeviceMockup } from '@modules/device-mockup';
import {
  FontConfigProvider,
  buildFallbackChains,
  collectActiveWebFonts,
  detectPlatform,
  getStudioWebFontsEnv,
  useFontConfig,
  type PlatformId,
} from '@modules/font-config';
import { ALL_STEP_KINDS } from '../factories';
import { templateTileKey } from '../mappers/templateIdentity';
import type { ThemeV2 } from '../types/themesV2.types';
import type { StepNode } from '@segmently/schema-models/v2';

declare global {
  interface Window {
    __SEGMENTLY_THEME_V2_EVAL_THEME__?: ThemeV2;
  }
}

// Upper bound for one Play session. Tiles report when their ENTRANCE ends
// (onEntranceComplete — Carousel/ProgressSteps do not hold it for their cycle),
// so anything still pending past this is a pathological tile.
const PREVIEW_SAFETY_MS = 12_000;

/** Why "Play animations" is disabled (also the wrapper's data-disabled-reason). */
type PlayDisabledReason = 'no-selection' | 'no-animation';

const PLAY_DISABLED_TOOLTIP: Record<PlayDisabledReason, string> = {
  'no-selection': 'Check the screens whose animations you want to play',
  'no-animation': 'Nothing to play: these screens have no entrance animation',
};

export interface ThemeV2PreviewCarouselProps {
  /** V2 Theme containing FlowDocument to preview */
  theme: ThemeV2;
  /** Callback when screen is selected */
  onScreenSelect?: (screenId: string) => void;
  /** Callback when screen should be edited */
  onScreenEdit?: (screenId: string, step: StepNode) => void;
  /** Compact mode - horizontal scroll, smaller mockups */
  compact?: boolean;
  /** Project ID used to load per-project font mappings */
  projectId?: string;
  /** Optional platform override for deterministic previews/tests */
  previewPlatform?: PlatformId;
  /** Opt-in: show per-tile selection toggles (selective theme apply) */
  selectable?: boolean;
  /**
   * Template tile keys currently selected for apply (controlled by parent).
   * A tile key is the bare StepKind for un-subtyped templates and
   * `kind::subtype` for authored variants (see `templateTileKey`), so two
   * same-kind subtyped tiles are selected independently.
   */
  selectedScreenTypes?: string[];
  /** Toggle a template tile key in the apply selection */
  onToggleScreenType?: (tileKey: string) => void;
  /** Replace the whole apply selection (used by Select/Deselect all) */
  onSetSelectedScreenTypes?: (tileKeys: string[]) => void;
  /** Per-tile "Apply for this type" shortcut; passes the tile's screenType, template screen id, and subtype */
  onApplyForType?: (screenType: string, templateScreenId?: string, subtype?: string) => void;
  /**
   * Opt-in: per-tile "clone this template into a new subtype" action (theme
   * editor authoring). When provided, each tile shows a copy icon that calls
   * this with the tile's StepNode; the host captures the subtype + persists.
   */
  onCloneTemplate?: (step: StepNode) => void;
  /**
   * UXR C9: per-tile delete for AUTHORED subtype templates (delete parity with
   * the block-subtype manager). Built-in tiles (no subtype) show a lock hint
   * instead — they are always available. The host confirms + persists.
   */
  onDeleteTemplate?: (step: StepNode) => void;
  /**
   * Called on every "Play animations" click (not on the automatic replay after an
   * entrance-setting change) — the theme editor plays its transition demo with it.
   */
  onPlayAnimations?: () => void;
}

/**
 * ThemeV2PreviewCarousel - Responsive grid of V2 screen previews
 */
export const ThemeV2PreviewCarousel: React.FC<ThemeV2PreviewCarouselProps> = ({
  theme,
  onScreenSelect,
  onScreenEdit,
  compact = false,
  projectId,
  previewPlatform,
  selectable = false,
  selectedScreenTypes,
  onToggleScreenType,
  onSetSelectedScreenTypes,
  onApplyForType,
  onCloneTemplate,
  onDeleteTemplate,
  onPlayAnimations,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // One-shot "Play animations" (owner 2026-09-29, like the screen editor's single-screen
  // play): a RISING edge of a tile's isAnimationPlaying replays its entrance once via
  // ScreenAnimationProvider's replayNonce (previewMode='edit' gates animations OFF
  // otherwise). playingIds = the tiles of the current session — a snapshot of the
  // playable (checked) tiles that have an entrance. Each reports onEntranceComplete once;
  // the last one ends the session.
  const [playingIds, setPlayingIds] = useState<ReadonlySet<string> | null>(null);
  const isAnimationPlaying = playingIds !== null;
  const pendingTilesRef = useRef(0);
  const safetyTimerRef = useRef<number | null>(null);
  const riseTimerRef = useRef<number | null>(null);
  const restartTimerRef = useRef<number | null>(null);
  const { config: fontConfig } = useFontConfig(projectId);

  const defaultScreenAnimation = (theme.flowDocument as {
    defaultScreenAnimation?: import('@segmently/schema-models/v2').ScreenAnimation;
  })?.defaultScreenAnimation;

  const platform = useMemo(
    () => previewPlatform ?? detectPlatform(
      typeof navigator !== 'undefined' ? navigator.userAgent : ''
    ),
    [previewPlatform],
  );

  const fontOverrides = useMemo(
    () => buildFallbackChains(fontConfig ?? null, platform),
    [fontConfig, platform],
  );

  const webFonts = useMemo(
    () => collectActiveWebFonts(fontConfig ?? null, {
      platforms: [platform],
      env: getStudioWebFontsEnv(),
    }),
    [fontConfig, platform],
  );

  // Opt-in Playwright eval hook; keeps production/runtime theme documents unchanged.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.localStorage.getItem('theme-v2-eval') !== '1') return undefined;

    window.__SEGMENTLY_THEME_V2_EVAL_THEME__ = theme;
    return () => {
      if (window.__SEGMENTLY_THEME_V2_EVAL_THEME__?.id === theme.id) {
        delete window.__SEGMENTLY_THEME_V2_EVAL_THEME__;
      }
    };
  }, [theme]);

  // Extract StepNode screens from FlowDocument, sort by StepKind order
  const screens = useMemo(() => {
    const screenEntries = Object.entries(theme.flowDocument?.screens || {});
    const allScreens = screenEntries.map(([id, step]) => ({
      id,
      step: step as StepNode,
    }));

    // Sort by ALL_STEP_KINDS order (same order as template factory). Same-kind
    // subtyped tiles get a stable secondary order by subtype so duplicates never
    // jitter between renders.
    return allScreens.sort((a, b) => {
      const orderA = ALL_STEP_KINDS.indexOf(a.step.screenType as typeof ALL_STEP_KINDS[number]);
      const orderB = ALL_STEP_KINDS.indexOf(b.step.screenType as typeof ALL_STEP_KINDS[number]);
      const byKind = (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
      if (byKind !== 0) return byKind;
      return (a.step.subtype ?? '').localeCompare(b.step.subtype ?? '');
    });
  }, [theme.flowDocument?.screens]);

  const hasScreens = screens.length > 0;
  const locale = theme.flowDocument?.defaultLanguage || 'en-US';

  // Tiles the button plays: the checked ones when tiles are selectable, all otherwise.
  const playableScreens = useMemo(
    () => (selectable
      ? screens.filter((s) => selectedScreenTypes?.includes(templateTileKey(s.step.screenType, s.step.subtype)))
      : screens),
    [screens, selectable, selectedScreenTypes],
  );

  // Of those, the tiles that have an entrance to play (the same resolver the tiles use).
  const animatedIds = useMemo(
    () => playableScreens
      .filter((s) => resolveScreenAnimationPlan(
        s.step.content, defaultScreenAnimation, s.step.screenType, locale,
      ).hasEntranceEntries)
      .map((s) => s.id),
    [playableScreens, defaultScreenAnimation, locale],
  );
  // Read by the delayed session starts, which must see the latest set.
  const animatedIdsRef = useRef(animatedIds);
  animatedIdsRef.current = animatedIds;

  const playDisabledReason: PlayDisabledReason | null =
    playableScreens.length === 0 ? 'no-selection' : animatedIds.length === 0 ? 'no-animation' : null;

  const stopPreview = useCallback(() => {
    pendingTilesRef.current = 0;
    if (safetyTimerRef.current !== null) {
      window.clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
    if (riseTimerRef.current !== null) {
      window.clearTimeout(riseTimerRef.current);
      riseTimerRef.current = null;
    }
    setPlayingIds(null);
  }, []);

  const beginSession = useCallback(() => {
    const ids = animatedIdsRef.current;
    if (ids.length === 0) return;
    pendingTilesRef.current = ids.length;
    safetyTimerRef.current = window.setTimeout(stopPreview, PREVIEW_SAFETY_MS);
    setPlayingIds(new Set(ids));
  }, [stopPreview]);

  // Drop, then rise on the next tick: a same-tick false→true batches into no change, and
  // a tile replays only on a RISING edge — so a click while playing restarts cleanly.
  const playOnce = useCallback(() => {
    stopPreview();
    riseTimerRef.current = window.setTimeout(() => {
      riseTimerRef.current = null;
      beginSession();
    }, 0);
  }, [stopPreview, beginSession]);

  const handlePlayClick = useCallback(() => {
    playOnce();
    onPlayAnimations?.();
  }, [playOnce, onPlayAnimations]);

  // Stable identity — this callback is an effect dependency inside every
  // tile's ScreenAnimationProvider; a new identity mid-session would restart
  // the tiles' completion timers.
  const handleTileEntranceComplete = useCallback(() => {
    if (pendingTilesRef.current <= 0) return; // stray completion outside a session
    pendingTilesRef.current -= 1;
    if (pendingTilesRef.current === 0) stopPreview();
  }, [stopPreview]);

  // Entrance settings changed (a motion scheme switch, an entrance edit) → play the new
  // entrances once, playing or not, so the user immediately SEES the change. The stamp
  // covers only entrance inputs, so transition-only edits never replay the tiles.
  const entranceStamp = useMemo(() => JSON.stringify({
    d: defaultScreenAnimation ?? null,
    s: screens.map(
      (s) => (s.step.content as { screenAnimation?: unknown } | undefined)?.screenAnimation ?? null,
    ),
  }), [defaultScreenAnimation, screens]);

  const prevStampRef = useRef(entranceStamp);
  useEffect(() => {
    const changed = prevStampRef.current !== entranceStamp;
    prevStampRef.current = entranceStamp;
    if (!changed) return;
    stopPreview();
    // Falling edge above resets every provider; the delayed rising edge below
    // replays with the fresh settings and coalesces rapid typing.
    if (restartTimerRef.current !== null) window.clearTimeout(restartTimerRef.current);
    restartTimerRef.current = window.setTimeout(() => {
      restartTimerRef.current = null;
      beginSession();
    }, 200);
  }, [entranceStamp, stopPreview, beginSession]);

  useEffect(() => () => {
    if (safetyTimerRef.current !== null) window.clearTimeout(safetyTimerRef.current);
    if (riseTimerRef.current !== null) window.clearTimeout(riseTimerRef.current);
    if (restartTimerRef.current !== null) window.clearTimeout(restartTimerRef.current);
  }, []);

  // Handle screen click
  const handleScreenClick = useCallback(
    (screenId: string, step: StepNode) => {
      setSelectedId(screenId);
      onScreenSelect?.(screenId);
      onScreenEdit?.(screenId, step);
    },
    [onScreenSelect, onScreenEdit]
  );

  // Format StepKind for display label
  const formatStepKind = (screenType: string): string => {
    return screenType
      .replace(/([A-Z])/g, ' $1')
      .trim();
  };

  if (!hasScreens) {
    return (
      <Box
        data-testid="theme-v2-preview-carousel"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No screens in V2 theme
        </Typography>
      </Box>
    );
  }

  return (
    <FontConfigProvider fontOverrides={fontOverrides} platform={platform} webFonts={webFonts}>
    <Box
      data-testid="theme-v2-preview-carousel"
      data-theme-v2-id={theme.id}
      data-theme-v2-name={theme.name}
      data-font-platform={platform}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
      }}
    >
      {/* Screen counter + select/deselect all (selective apply mode) */}
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {screens.length} screen{screens.length !== 1 ? 's' : ''}
          </Typography>
          {/* One-shot play: re-clickable while playing (restarts); disabled with the
              reason when nothing would play. The span keeps the tooltip on a disabled button. */}
          <Tooltip title={playDisabledReason ? PLAY_DISABLED_TOOLTIP[playDisabledReason] : ''}>
            <span
              data-testid="theme-anim-play-btn-wrapper"
              data-disabled-reason={playDisabledReason ?? undefined}
            >
              <Button
                size="small"
                variant="outlined"
                color={isAnimationPlaying ? 'primary' : 'inherit'}
                data-testid="theme-anim-play-btn"
                data-playing={isAnimationPlaying ? 'true' : 'false'}
                startIcon={<PlayArrowIcon />}
                disabled={playDisabledReason !== null}
                onClick={handlePlayClick}
                sx={{ textTransform: 'none', py: 0, minWidth: 'auto' }}
              >
                {isAnimationPlaying ? 'Playing…' : 'Play animations'}
              </Button>
            </span>
          </Tooltip>
          {selectable && onSetSelectedScreenTypes && (() => {
            const allKeys = Array.from(
              new Set(screens.map((s) => templateTileKey(s.step.screenType, s.step.subtype))),
            );
            const allSelected =
              allKeys.length > 0 &&
              allKeys.every((t) => selectedScreenTypes?.includes(t));
            return (
              <Button
                size="small"
                variant="text"
                data-testid="apply-theme-select-all-btn"
                onClick={() => onSetSelectedScreenTypes(allSelected ? [] : allKeys)}
                sx={{ textTransform: 'none', py: 0, minWidth: 'auto' }}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </Button>
            );
          })()}
        </Box>
      )}

      {/* Responsive grid of screen previews */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: compact ? 'nowrap' : 'wrap',
          gap: compact ? 2 : 3,
          justifyContent: compact ? 'flex-start' : 'center',
          alignItems: 'flex-start',
          p: compact ? 1 : 2,
          overflowX: compact ? 'auto' : 'visible',
        }}
      >
        {screens.map((item) => (
          <Box
            key={item.id}
            data-testid={`v2-screen-preview-${item.id}`}
            data-theme-v2-screen-id={item.id}
            data-screen-type={item.step.screenType}
            onClick={compact ? undefined : () => handleScreenClick(item.id, item.step)}
            sx={{
              position: 'relative',
              cursor: compact ? 'default' : 'pointer',
              transition: compact ? 'none' : 'all 0.2s ease',
              border: compact ? 'none' : (selectedId === item.id ? '2px solid' : '2px solid transparent'),
              borderColor: compact ? 'transparent' : (selectedId === item.id ? 'primary.main' : 'transparent'),
              borderRadius: compact ? 0 : 2,
              p: compact ? 0 : 1,
              pointerEvents: compact ? 'none' : 'auto',
              '&:hover': {
                transform: compact ? 'none' : 'scale(1.02)',
                boxShadow: compact ? 0 : 3,
              },
            }}
          >
            {/* Selective-apply controls (opt-in) */}
            {selectable && !compact && (
              <Checkbox
                size="small"
                checked={selectedScreenTypes?.includes(templateTileKey(item.step.screenType, item.step.subtype)) ?? false}
                onClick={(event) => event.stopPropagation()}
                onChange={() => onToggleScreenType?.(templateTileKey(item.step.screenType, item.step.subtype))}
                data-testid={`apply-theme-tile-toggle-${templateTileKey(item.step.screenType, item.step.subtype)}`}
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  p: 0.25,
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              />
            )}
            {/* Clone-into-subtype (theme editor authoring) */}
            {onCloneTemplate && !compact && (
              <Tooltip title="Clone as a new subtype">
                <IconButton
                  size="small"
                  data-testid={`theme-v2-tile-clone-${item.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onCloneTemplate(item.step);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    p: 0.25,
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* UXR C9: authored subtype tiles are deletable; built-ins show a lock. */}
            {onDeleteTemplate && !compact && (
              item.step.subtype?.trim() ? (
                <Tooltip title="Delete this subtype template">
                  <IconButton
                    size="small"
                    data-testid={`theme-v2-tile-delete-${item.id}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteTemplate(item.step);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 32,
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      p: 0.25,
                      color: 'error.main',
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Built-in template — always available">
                  <Box
                    data-testid={`theme-v2-tile-lock-${item.id}`}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 34,
                      zIndex: 2,
                      color: 'text.disabled',
                      display: 'flex',
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                </Tooltip>
              )
            )}
            {/* Device mockup with V2 screen content */}
            <Box
              data-testid={`v2-phone-mockup-${item.id}`}
              sx={{
                width: compact ? 129 : { xs: 160, sm: 180, md: 200, lg: 220 },
                height: compact ? 280 : { xs: 340, sm: 380, md: 420, lg: 460 },
                flexShrink: 0,
              }}
            >
              <DeviceMockup
                device="iphone"
                autoScale={true}
                scaleOptions={{ maxScale: 0.5, padding: 4 }}
              >
                <AutoScaleScreenContainer>
                  {/* editMode is flipped on ONLY while THIS tile plays: screen
                      components emit element editPaths (the animation mount
                      keys — see HeroContent `ep` gating) only in editMode or
                      live mode. Idle tiles keep the clean non-edit DOM. No
                      onAnimationComplete: Carousel/ProgressSteps would hold the
                      session for their cycle — the entrance end is the signal. */}
                  <V2ScreenView
                    step={item.step}
                    locale={locale}
                    previewMode="edit"
                    editMode={playingIds?.has(item.id) ?? false}
                    isAnimationPlaying={playingIds?.has(item.id) ?? false}
                    defaultScreenAnimation={defaultScreenAnimation}
                    onEntranceComplete={handleTileEntranceComplete}
                  />
                </AutoScaleScreenContainer>
              </DeviceMockup>
            </Box>

            {/* StepKind label + optional subtype badge */}
            {!compact && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  mt: 1,
                  color: 'text.secondary',
                  maxWidth: { xs: 160, sm: 180, md: 200, lg: 220 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStepKind(item.step.screenType)}
                {item.step.subtype?.trim() ? (
                  <Box
                    component="span"
                    data-testid={`theme-v2-tile-subtype-${item.id}`}
                    sx={{
                      ml: 0.75,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 1,
                      bgcolor: 'action.selected',
                      color: 'text.primary',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                    }}
                  >
                    {item.step.subtype.trim()}
                  </Box>
                ) : null}
              </Typography>
            )}

            {/* Per-tile apply shortcut (opt-in) */}
            {selectable && !compact && onApplyForType && (
              <Button
                size="small"
                variant="text"
                onClick={(event) => {
                  event.stopPropagation();
                  onApplyForType(item.step.screenType, item.id, item.step.subtype);
                }}
                data-testid={`apply-theme-tile-apply-${templateTileKey(item.step.screenType, item.step.subtype)}`}
                sx={{
                  display: 'block',
                  mx: 'auto',
                  fontSize: '0.7rem',
                  textTransform: 'none',
                }}
              >
                Apply for this type
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
    </FontConfigProvider>
  );
};

export default ThemeV2PreviewCarousel;
